<template>
  <div class="container-fluid">
    <CCard class="p-3" style="margin: 0; width: 100%;">
      <CRow class="align-items-center mb-3">
        <CCol>
          <h4 class="color-light page_title">Auto Mailer Reports</h4>
        </CCol>
        <CCol class="text-right">
          <CButton color="primary" @click="openAddReportModal">
            Add New Auto Mail
          </CButton>
        </CCol>
      </CRow>

      <!-- Table to display saved report settings -->
      <CRow>
        <CCol>
          <div class="table-responsive" style="width: 100%;">
            <table class="table table-striped table-hover" style="width: 100%;">
              <thead>
                <tr>
                  <th style="width: 15%;">Mail Format Name</th>
                  <th style="width: 10%;">Frequency</th>
                  <th style="width: 10%;">Timezone</th>
                  <th style="width: 20%;">Recipients</th>
                  <th style="width: 15%;">Campaigns</th>
                  <th style="width: 10%;">Last Executed</th>
                  <th style="width: 10%;">Next Execution</th>
                  <th style="width: 5%;">Status</th>
                  <th style="width: 5%; min-width: 150px;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="reportSettings.length === 0">
                  <td colspan="9" class="text-center">No automatic mail formats configured</td>
                </tr>
                <tr v-for="(report, index) in reportSettings" :key="index">
                <td>{{ report.name }}</td>
                <td>{{ report.reportFrequency.text }}</td>
                <td>{{ report.timezone || 'UTC' }}</td>
                <td>
                  <span v-if="report.reportRecipientsForAuto.length <= 2">
                    {{ report.reportRecipientsForAuto.join(', ') }}
                  </span>
                  <span v-else>
                    {{ report.reportRecipientsForAuto.slice(0, 2).join(', ') }}
                    <small class="text-muted">+{{ report.reportRecipientsForAuto.length - 2 }} more</small>
                  </span>
                </td>
                <td>
                  <span v-if="report.selectedCampaigns.length === 0">All</span>
                  <span v-else-if="report.selectedCampaigns.length <= 2">
                    {{ report.selectedCampaigns.map(c => c.text).join(', ') }}
                  </span>
                  <span v-else>
                    {{ report.selectedCampaigns.slice(0, 2).map(c => c.text).join(', ') }}
                    <small class="text-muted">+{{ report.selectedCampaigns.length - 2 }} more</small>
                  </span>
                </td>
                <td>
                  <span v-if="report.lastExecutedAt">
                    {{ formatDateTime(report.lastExecutedAt) }}
                  </span>
                  <span v-else class="text-muted">Never</span>
                </td>
                <td>
                  <span v-if="report.nextExecutionAt && report.enabled">
                    {{ formatDateTime(report.nextExecutionAt) }}
                    <br>
                    <small class="text-muted">(+15 min delay)</small>
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <CSwitch 
                    color="success" 
                    labelOn="ON" 
                    labelOff="OFF" 
                    :checked="report.enabled"
                    @update:checked="toggleReportStatus(index)" 
                    :disabled="loading"
                  />
                </td>
                <td>
                  <div class="action-buttons" style="white-space: nowrap;">
                    <button 
                      @click="editReport(index)" 
                      class="action-btn edit-btn"
                      title="Edit Report"
                    >
                      <span class="icon-text">✏️</span>
                    </button>
                    <button 
                      @click="cloneReport(index)" 
                      class="action-btn clone-btn"
                      title="Clone Report"
                    >
                      <span class="icon-text">📋</span>
                    </button>
                    <button 
                      @click="viewExecutionHistory(index)" 
                      class="action-btn history-btn"
                      title="View Execution History"
                    >
                      <span class="icon-text">📊</span>
                    </button>
                    <button 
                      @click="executeReportNow(index)" 
                      class="action-btn execute-btn"
                      :disabled="!report.enabled"
                      :title="report.enabled ? 'Execute Now' : 'Enable report first'"
                    >
                      <span class="icon-text">▶</span>
                    </button>
                    <button 
                      @click="deleteReport(index)" 
                      class="action-btn delete-btn"
                      title="Delete Report"
                    >
                      <span class="icon-text">🗑️</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </CCol>
      </CRow>
    </CCard>

    <!-- Modal for Add/Edit Report -->
    <CModal 
      :show.sync="showAddReportModal" 
      :title="editingIndex !== null ? 'Edit Auto Mailer Report' : 'Add New Auto Mailer Report'"
      size="xl"
      :closeOnBackdrop="false"
    >
      <div class="modal-content-wrapper">
        <!-- Basic Information Section -->
        <CCard class="mb-3">
          <CCardBody>
            <h5 class="card-title mb-3">Basic Information</h5>
            <CRow>
              <CCol class="col-md-6">
                <div class="form-group">
                  <label class="form-label required">Title</label>
                  <CInput 
                    v-model="currentReport.name" 
                    placeholder="Enter a descriptive name for this mail format"
                  />
                </div>
              </CCol>
              <CCol class="col-md-3">
                <div class="form-group">
                  <label class="form-label required">Frequency</label>
                  <multiselect
                    v-model="currentReport.reportFrequency"
                    :options="frequencyOptions"
                    :multiple="false"
                    :clear-on-select="false"
                    :close-on-select="true"
                    track-by="value"
                    label="text"
                    :searchable="false"
                    placeholder="Select frequency"
                    :preselect-first="false"
                  ></multiselect>
                  <!-- Note: Cron jobs execute with a 15-minute delay (e.g., 00:15 for midnight schedule) -->
                  <small class="text-muted">
                    Note: Reports execute ~15 minutes after scheduled time<br>
                    • Hourly: Every hour at :15<br>
                    • Daily: Midnight at 00:15<br>
                    • Weekly: Sunday at 00:15<br>
                    • Monthly: 1st day at 00:15
                  </small>
                </div>
              </CCol>
              <CCol class="col-md-3">
                <div class="form-group">
                  <label class="form-label">Timezone</label>
                  <multiselect
                    v-model="currentReport.timezone"
                    :options="timezone_list"
                    :multiple="false"
                    :clear-on-select="false"
                    :close-on-select="true"
                    :searchable="true"
                    placeholder="Search and select timezone"
                    :preselect-first="false"
                  ></multiselect>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Recipients and Campaigns Section -->
        <CCard class="mb-3">
          <CCardBody>
            <h5 class="card-title mb-3">Recipients & Campaigns</h5>
            <CRow class="mb-3">
              <CCol class="col-md-6">
                <div class="form-group">
                  <label class="form-label required">Email Recipients</label>
                  <multiselect
                    v-model="currentReport.reportRecipientsForAuto"
                    :options="currentReport.reportRecipientsForAuto"
                    :multiple="true"
                    :taggable="true"
                    @tag="addEmailTag"
                    :clear-on-select="false"
                    :close-on-select="false"
                    placeholder="Type email and press enter"
                    :preselect-first="false"
                    :hide-selected="false"
                    tag-placeholder="Press enter to add"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                  ></multiselect>
                  <small class="text-muted">Add email addresses</small>
                </div>
              </CCol>
              <CCol class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Select Campaigns</label>
                  <multiselect
                    v-model="currentReport.selectedCampaigns"
                    :options="campaignOptions"
                    :multiple="true"
                    :clear-on-select="true"
                    :close-on-select="false"
                    track-by="text"
                    label="text"
                    :preserve-search="true"
                    placeholder="Select campaigns (empty = all)"
                    :preselect-first="false"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    class="campaign-selector"
                  ></multiselect>
                  <small class="text-muted">Leave empty for all campaigns</small>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Report Configuration Section -->
        <CCard class="mb-3">
          <CCardBody>
            <h5 class="card-title mb-3">Report Configuration</h5>
            <CRow>
              <CCol class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Group By</label>
                  <multiselect
                    v-model="currentReport.selectedGroupBy"
                    :options="projectionOptions"
                    :multiple="true"
                    :clear-on-select="true"
                    :close-on-select="false"
                    track-by="value"
                    label="text"
                    :preserve-search="false"
                    placeholder="Select grouping options"
                    :preselect-first="false"
                    :limit="2"
                    :limit-text="count => `and ${count} more`"
                  ></multiselect>
                  <small class="text-muted">Choose how to group your report data</small>
                </div>
              </CCol>
              <CCol class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Metrics to Include</label>
                  <multiselect
                    v-model="currentReport.selectedMetrics"
                    :options="metricsOptions"
                    :multiple="true"
                    :clear-on-select="true"
                    :close-on-select="false"
                    track-by="value"
                    label="text"
                    :preserve-search="false"
                    placeholder="Select metrics to display"
                    :preselect-first="false"
                    :limit="3"
                    :limit-text="count => `and ${count} more metrics`"
                  ></multiselect>
                  <small class="text-muted">Select the metrics you want in your report</small>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Custom Graphs Section -->
        <CCard class="mb-3">
          <CCardBody>
            <h5 class="card-title mb-3">Custom Graphs Configuration</h5>
            <CRow class="mb-3">
              <CCol>
                <CButton
                  color="info"
                  size="sm"
                  @click="addCustomGraph"
                  :disabled="currentReport.customGraphs && currentReport.customGraphs.length >= 10"
                >
                  <i class="fa fa-plus"></i> Add Custom Graph
                </CButton>
                <small class="text-muted ml-2">Add up to 10 custom graphs to your report</small>
              </CCol>
            </CRow>

            <!-- Custom Graph Items -->
            <div v-for="(graph, index) in currentReport.customGraphs" :key="index" class="custom-graph-item mb-3">
              <CCard class="border-left-info">
                <CCardBody>
                  <CRow class="mb-2">
                    <CCol class="col-md-10">
                      <h6 class="graph-title">Custom Graph {{ index + 1 }}</h6>
                    </CCol>
                    <CCol class="col-md-2 text-right">
                      <CButton
                        color="danger"
                        size="sm"
                        @click="removeCustomGraph(index)"
                        variant="outline"
                      >
                        <i class="fa fa-trash"></i> Remove
                      </CButton>
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol class="col-md-6">
                      <div class="form-group">
                        <label class="form-label required">Graph Name</label>
                        <CInput
                          v-model="graph.name"
                          placeholder="Enter graph name"
                          :class="{ 'is-invalid': !graph.name && graphValidationTrigger }"
                        />
                        <small class="text-danger" v-if="!graph.name && graphValidationTrigger">Graph name is required</small>
                      </div>
                    </CCol>
                    <CCol class="col-md-6">
                      <div class="form-group">
                        <label class="form-label">Description</label>
                        <CInput
                          v-model="graph.description"
                          placeholder="Brief description (optional)"
                        />
                      </div>
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol class="col-md-6">
                      <div class="form-group">
                        <label class="form-label required">Graph Type</label>
                        <multiselect
                          v-model="graph.type"
                          :options="graphTypeOptions"
                          :multiple="false"
                          :clear-on-select="false"
                          :close-on-select="true"
                          track-by="value"
                          label="text"
                          :searchable="false"
                          placeholder="Select graph type"
                          :preselect-first="false"
                          :class="{ 'is-invalid': !graph.type && graphValidationTrigger }"
                          @select="onGraphTypeChange(graph)"
                        ></multiselect>
                        <small class="text-danger" v-if="!graph.type && graphValidationTrigger">Graph type is required</small>
                        <small class="text-muted" v-else>Choose how your data will be visualized</small>
                      </div>
                    </CCol>
                    <CCol class="col-md-6">
                      <div class="alert alert-info mb-3">
                        <i class="fa fa-info-circle"></i>
                        <strong>Selected:</strong> {{ graph.type ? graph.type.text : 'No type selected' }}
                        <br>
                        <small>{{ graph.type ? graph.type.description : 'Please select a graph type to see description' }}</small>
                      </div>
                    </CCol>
                  </CRow>

                  <!-- Graph Data Selection -->
                  <CRow>
                    <CCol class="col-md-12">
                      <div class="form-group">
                        <label class="form-label">Select Campaigns for this Graph</label>
                        <multiselect
                          v-model="graph.selectedCampaigns"
                          :options="currentReport.selectedCampaigns.length > 0 ? currentReport.selectedCampaigns : campaignOptions"
                          :searchable="true"
                          :multiple="true"
                          :placeholder="'Choose from ' + (currentReport.selectedCampaigns.length > 0 ? 'report campaigns' : 'all campaigns')"
                          label="text"
                          track-by="value"
                          :close-on-select="false"
                          :clear-on-select="false"
                          :preserve-search="true"
                        >
                          <template slot="noResult">No campaigns found</template>
                        </multiselect>
                        <small class="text-muted">Leave empty to use all campaigns from report configuration</small>
                      </div>
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol class="col-md-6" v-if="graph.type && graph.type.value !== 'line'">
                      <div class="form-group">
                        <label class="form-label required">Group By (Dimensions)</label>
                        <multiselect
                          v-model="graph.dimensions"
                          :options="currentReport.selectedGroupBy.length > 0 ? currentReport.selectedGroupBy : projectionOptions"
                          :searchable="true"
                          :multiple="false"
                          :placeholder="'Select one dimension'"
                          label="text"
                          track-by="value"
                          :close-on-select="true"
                          :clear-on-select="false"
                          :preserve-search="true"
                          :class="{ 'is-invalid': !graph.dimensions && graphValidationTrigger }"
                        >
                          <template slot="noResult">No fields found</template>
                        </multiselect>
                        <small class="text-danger" v-if="!graph.dimensions && graphValidationTrigger">
                          Please select one dimension
                        </small>
                        <small class="text-muted" v-else>
                          Select one dimension for grouping data
                        </small>
                      </div>
                    </CCol>
                    <CCol class="col-md-6" v-else-if="graph.type && graph.type.value === 'line'">
                      <div class="alert alert-info">
                        <i class="fa fa-info-circle"></i>
                        <strong>Line Chart X-Axis:</strong> Date/Time
                        <br>
                        <small>Line charts automatically use date/time for the X-axis to show trends over time</small>
                      </div>
                    </CCol>
                    <CCol :class="graph.type && graph.type.value === 'line' ? 'col-md-6' : 'col-md-6'">
                      <div class="form-group">
                        <label class="form-label required">Metrics</label>
                        <multiselect
                          v-model="graph.metrics"
                          :options="currentReport.selectedMetrics.length > 0 ? currentReport.selectedMetrics : metricsOptions"
                          :searchable="true"
                          :multiple="graph.type && graph.type.value === 'pie' ? false : true"
                          :placeholder="graph.type && graph.type.value === 'pie' ? 'Select one metric for pie chart' : ('Select from ' + (currentReport.selectedMetrics.length > 0 ? 'report metrics' : 'available metrics'))"
                          label="text"
                          track-by="value"
                          :close-on-select="graph.type && graph.type.value === 'pie' ? true : false"
                          :clear-on-select="false"
                          :preserve-search="true"
                          :class="{ 'is-invalid': (!graph.metrics || graph.metrics.length === 0) && graphValidationTrigger }"
                        >
                          <template slot="noResult">No metrics found</template>
                        </multiselect>
                        <small class="text-danger" v-if="(!graph.metrics || graph.metrics.length === 0) && graphValidationTrigger">
                          {{ graph.type && graph.type.value === 'pie' ? 'Please select one metric' : 'Please select at least one metric' }}
                        </small>
                        <small class="text-muted" v-else>
                          {{ graph.type && graph.type.value === 'pie' ? 'Select one metric for pie chart' : 'Select from report\'s configured metrics' }}
                        </small>
                      </div>
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol class="col-md-6">
                      <div class="form-group">
                        <label class="form-label">Show Legend</label>
                        <CSwitch
                          color="success"
                          labelOn="YES"
                          labelOff="NO"
                          :checked="graph.showLegend"
                          @update:checked="val => graph.showLegend = val"
                        />
                      </div>
                    </CCol>
                    <CCol class="col-md-6">
                      <div class="form-group">
                        <label class="form-label">Show Data Labels</label>
                        <CSwitch
                          color="success"
                          labelOn="YES"
                          labelOff="NO"
                          :checked="graph.showDataLabels"
                          @update:checked="val => graph.showDataLabels = val"
                        />
                      </div>
                    </CCol>
                  </CRow>

                  <!-- Graph Help Section -->
                  <CRow class="mt-3">
                    <CCol>
                      <CButton
                        color="info"
                        size="sm"
                        @click="showGraphStructureHelp(graph)"
                      >
                        <i class="fa fa-question-circle"></i> How will this look?
                      </CButton>
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>
            </div>

            <div v-if="!currentReport.customGraphs || currentReport.customGraphs.length === 0" class="text-center py-3">
              <p class="text-muted">No custom graphs configured. Click "Add Custom Graph" to create one.</p>
            </div>
          </CCardBody>
        </CCard>
      </div>
      
      <template #footer>
        <CButton color="secondary" @click="cancelEdit">Cancel</CButton>
        <CButton color="success" @click="saveReport" :disabled="loading">
          {{ editingIndex !== null ? 'Update' : 'Save' }}
        </CButton>
      </template>
    </CModal>

    <!-- Execution History Modal -->
    <CModal 
      :show.sync="showExecutionHistoryModal" 
      title="Execution History"
      size="xl"
      :closeOnBackdrop="false"
    >
      <div class="execution-history-wrapper">
        <CCard class="border-0">
          <CCardBody>
            <div v-if="currentReportHistory">
              <h5 class="mb-3">{{ currentReportHistory.name }} - Execution Logs</h5>
              
              <!-- Summary Stats -->
              <CRow class="mb-4">
                <CCol class="col-md-3">
                  <div class="stat-card text-center">
                    <div class="stat-value">{{ executionStats.total }}</div>
                    <div class="stat-label">Total Executions</div>
                  </div>
                </CCol>
                <CCol class="col-md-3">
                  <div class="stat-card text-center success">
                    <div class="stat-value">{{ executionStats.successful }}</div>
                    <div class="stat-label">Successful</div>
                  </div>
                </CCol>
                <CCol class="col-md-3">
                  <div class="stat-card text-center warning">
                    <div class="stat-value">{{ executionStats.pending }}</div>
                    <div class="stat-label">In Progress</div>
                  </div>
                </CCol>
                <CCol class="col-md-3">
                  <div class="stat-card text-center danger">
                    <div class="stat-value">{{ executionStats.failed }}</div>
                    <div class="stat-label">Failed</div>
                  </div>
                </CCol>
              </CRow>

              <!-- Filters -->
              <CRow class="mb-3">
                <CCol class="col-md-4">
                  <label class="form-label">Date Range</label>
                  <CInput
                    type="date"
                    v-model="historyFilters.startDate"
                    @change="reloadExecutionHistory"
                  />
                </CCol>
                <CCol class="col-md-4">
                  <label class="form-label">&nbsp;</label>
                  <CInput
                    type="date"
                    v-model="historyFilters.endDate"
                    @change="reloadExecutionHistory"
                  />
                </CCol>
                <CCol class="col-md-4">
                  <label class="form-label">Status Filter</label>
                  <CSelect
                    v-model="historyFilters.status"
                    :options="statusFilterOptions"
                    @change="filterExecutionHistory"
                  />
                </CCol>
              </CRow>

              <!-- Execution History Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Execution ID</th>
                      <th>Status</th>
                      <th>Started At</th>
                      <th>Completed At</th>
                      <th>Duration</th>
                      <th>Recipients</th>
                      <th>Emails Sent</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredExecutionHistory.length === 0">
                      <td colspan="8" class="text-center">No execution history available</td>
                    </tr>
                    <tr v-for="(execution, index) in filteredExecutionHistory" :key="index">
                      <td>
                        <code>{{ execution.executionId }}</code>
                      </td>
                      <td>
                        <CBadge 
                          :color="getStatusColor(execution.status)"
                        >
                          {{ execution.status }}
                        </CBadge>
                      </td>
                      <td>{{ formatDateTime(execution.startedAt) }}</td>
                      <td>
                        <span v-if="execution.completedAt">
                          {{ formatDateTime(execution.completedAt) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span v-if="execution.duration">
                          {{ formatDuration(execution.duration) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>{{ execution.recipientCount || 0 }}</td>
                      <td>
                        <span v-if="execution.emailsSent !== undefined">
                          {{ execution.emailsSent }}
                          <span v-if="execution.recipientCount && execution.emailsSent < execution.recipientCount" class="text-danger">
                            ({{ execution.recipientCount - execution.emailsSent }} failed)
                          </span>
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <CButton 
                          color="info" 
                          size="sm" 
                          @click="viewExecutionDetails(execution)"
                        >
                          View Details
                        </CButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <CRow class="mt-3" v-if="executionHistoryPagination.totalPages > 1">
                <CCol>
                  <nav>
                    <ul class="pagination">
                      <li class="page-item" :class="{ disabled: executionHistoryPagination.page === 1 }">
                        <a class="page-link" @click="loadExecutionHistory(executionHistoryPagination.page - 1)">
                          Previous
                        </a>
                      </li>
                      <li 
                        class="page-item" 
                        v-for="page in executionHistoryPagination.totalPages" 
                        :key="page"
                        :class="{ active: page === executionHistoryPagination.page }"
                      >
                        <a class="page-link" @click="loadExecutionHistory(page)">{{ page }}</a>
                      </li>
                      <li class="page-item" :class="{ disabled: executionHistoryPagination.page === executionHistoryPagination.totalPages }">
                        <a class="page-link" @click="loadExecutionHistory(executionHistoryPagination.page + 1)">
                          Next
                        </a>
                      </li>
                    </ul>
                  </nav>
                </CCol>
              </CRow>
            </div>
            <div v-else class="text-center py-5">
              <i class="fa fa-spinner fa-spin fa-3x text-muted"></i>
              <p class="mt-3">Loading execution history...</p>
            </div>
          </CCardBody>
        </CCard>
      </div>
      
      <template #footer>
        <CButton color="secondary" @click="showExecutionHistoryModal = false">Close</CButton>
        <CButton color="primary" @click="refreshExecutionHistory" :disabled="loading">
          <i class="fa fa-refresh"></i> Refresh
        </CButton>
      </template>
    </CModal>

    <!-- Execution Details Modal -->
    <CModal 
      :show.sync="showExecutionDetailsModal" 
      title="Execution Details"
      size="lg"
      :closeOnBackdrop="false"
    >
      <div v-if="selectedExecutionDetails">
        <CCard class="mb-3">
          <CCardBody>
            <h6 class="card-subtitle mb-3 text-muted">Execution Information</h6>
            <CRow>
              <CCol class="col-md-6">
                <p><strong>Execution ID:</strong> <code>{{ selectedExecutionDetails.executionId }}</code></p>
                <p><strong>Status:</strong> 
                  <CBadge :color="getStatusColor(selectedExecutionDetails.status)">
                    {{ selectedExecutionDetails.status }}
                  </CBadge>
                </p>
                <p><strong>Started:</strong> {{ formatDateTime(selectedExecutionDetails.startedAt) }}</p>
              </CCol>
              <CCol class="col-md-6">
                <p><strong>Completed:</strong> {{ formatDateTime(selectedExecutionDetails.completedAt) || '-' }}</p>
                <p><strong>Duration:</strong> {{ formatDuration(selectedExecutionDetails.duration) || '-' }}</p>
                <p><strong>Trigger:</strong> {{ selectedExecutionDetails.trigger || 'Scheduled' }}</p>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard class="mb-3">
          <CCardBody>
            <h6 class="card-subtitle mb-3 text-muted">Email Delivery Summary</h6>
            <CRow>
              <CCol class="col-md-4">
                <p><strong>Total Recipients:</strong> {{ selectedExecutionDetails.recipientCount || 0 }}</p>
              </CCol>
              <CCol class="col-md-4">
                <p><strong>Emails Sent:</strong> {{ selectedExecutionDetails.emailsSent || 0 }}</p>
              </CCol>
              <CCol class="col-md-4">
                <p><strong>Failed:</strong> 
                  <span class="text-danger">{{ selectedExecutionDetails.failedCount || 0 }}</span>
                </p>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard v-if="selectedExecutionDetails.errors && selectedExecutionDetails.errors.length > 0">
          <CCardBody>
            <h6 class="card-subtitle mb-3 text-muted">Error Details</h6>
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Error</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(error, index) in selectedExecutionDetails.errors" :key="index">
                    <td>{{ error.recipient }}</td>
                    <td class="text-danger">{{ error.error }}</td>
                    <td>{{ formatDateTime(error.timestamp) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>

        <CCard v-if="selectedExecutionDetails.logs && selectedExecutionDetails.logs.length > 0">
          <CCardBody>
            <h6 class="card-subtitle mb-3 text-muted">Execution Logs</h6>
            <div class="log-viewer">
              <pre class="log-content">{{ selectedExecutionDetails.logs }}</pre>
            </div>
          </CCardBody>
        </CCard>
      </div>
      
      <template #footer>
        <CButton color="secondary" @click="showExecutionDetailsModal = false">Close</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";
import { CRow, CCol, CCard, CCardBody, CButton, CModal, CInput, CSwitch, CImg, CSelect, CBadge } from "@coreui/vue";
import Multiselect from "vue-multiselect";
import DashboardService from "./../../services/dashboardController";
import MailerReportsService from "./../../services/mailerReportsService";
import SettingService from "./../../services/settingControlService";

export default {
  name: 'AutoMailerReports',
  components: { 
    CRow, 
    CCol, 
    CCard,
    CCardBody,
    CButton, 
    CModal, 
    CInput, 
    CSwitch,
    CImg,
    CSelect,
    CBadge,
    Multiselect 
  },
  data() {
    return {
      loading: false,
      showAddReportModal: false,
      showExecutionHistoryModal: false,
      showExecutionDetailsModal: false,
      editingIndex: null,
      reportSettings: [],
      currentReportHistory: null,
      executionHistory: [],
      filteredExecutionHistory: [],
      selectedExecutionDetails: null,
      executionStats: {
        total: 0,
        successful: 0,
        failed: 0,
        pending: 0
      },
      executionHistoryPagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },
      historyFilters: {
        startDate: '',
        endDate: '',
        status: 'all'
      },
      statusFilterOptions: [
        { value: 'all', text: 'All Status' },
        { value: 'completed', text: 'Completed' },
        { value: 'failed', text: 'Failed' },
        { value: 'pending', text: 'In Progress' },
        { value: 'scheduled', text: 'Scheduled' }
      ],
      currentReport: {
        _id: null,
        name: '',
        enabled: true,
        reportFrequency: { value: 'daily', text: 'Daily' },
        timezone: 'UTC',
        reportRecipientsForAuto: [],
        selectedCampaigns: [],
        selectedGroupBy: [
          { value: "campname", text: "Campaign" }
        ],
        selectedMetrics: [
          { value: "browse", text: "Click" },
          { value: "impression", text: "Impression" },
          { value: "accepted", text: "Valid" },
          { value: "discarded", text: "Discarded" },
          { value: "rejected", text: "Invalid" },
          { value: "converted", text: "Converted" },
          { value: "convertedRate", text: "Conversion Rate" },
          { value: "rejectedRate", text: "Invalid Rate" },
          { value: "acceptedRate", text: "Valid Rate" }
        ],
        customGraphs: []
      },
      graphValidationTrigger: false,
      frequencyOptions: [
        { value: 'hourly', text: 'Hourly' },
        { value: 'daily', text: 'Daily' },
        { value: 'weekly', text: 'Weekly' },
        { value: 'monthly', text: 'Monthly' }
      ],
      campaignOptions: [],
      camp_list: {},
      projectionOptions: [
        { value: "campname", text: "Campaign" },
        { value: "offerid", text: "Offer" },
        { value: "tsid", text: "TrafficSource" },
        { value: "country", text: "Country" },
        { value: "browser", text: "Browser" },
        { value: "os", text: "Operating System" },
        { value: "devicetype", text: "Device Type" },
        { value: "networktype", text: "Network Type" },
        { value: "packageid", text: "Package Id"},
        { value: "external_id", text: "External Id" },
        { value: "carrier", text: "Carrier" },
        { value: "languages", text: "Languages" },
        { value: "timezone", text: "TimeZone" },
        { value: "make", text: "Make" },
        { value: "model", text: "Model" },
        { value: "eventMessage", text: "Event Message" },
        { value: "qp_sub1", text: "Sub1" },
        { value: "qp_sub2", text: "Sub2" },
        { value: "qp_sub3", text: "Sub3" },
        { value: "qp_sub4", text: "Sub4" },
        { value: "qp_sub5", text: "Sub5" },
        { value: "qp_sub6", text: "Sub6" },
        { value: "qp_sub7", text: "Sub7" },
        { value: "qp_sub8", text: "Sub8" },
        { value: "qp_sub9", text: "Sub9" },
        { value: "qp_sub10", text: "Sub10" },
        { value: "qp_sub11", text: "Sub11" },
        { value: "qp_sub12", text: "Sub12" }
      ],
      timezone_list: Intl.supportedValuesOf('timeZone'),
      metricsOptions: [
        { value: "browse", text: "Click" },
        { value: "render", text: "Render" },
        { value: "impression", text: "Impression" },
        { value: "accepted", text: "Valid" },
        { value: "discarded", text: "Discarded" },
        { value: "rejected", text: "Invalid" },
        { value: "convertedRate", text: "Conversion Rate" },
        { value: "converted", text: "Converted" },
        { value: "offerrev", text: "Offer Revenue" },
        { value: "camprev", text: "Campaign Revenue" },
        { value: "event", text: "Event Count" },
        { value: "renderRate", text: "Render Rate" },
        { value: "impressionRate", text: "Impression Rate" },
        { value: "rejectedRate", text: "Invalid Rate" },
        { value: "acceptedRate", text: "Valid Rate" }
      ],
      graphTypeOptions: [
        {
          value: "bar",
          text: "Bar Chart",
          description: "Vertical bars showing data comparison across categories"
        },
        {
          value: "pie",
          text: "Pie Chart",
          description: "Circular chart showing proportional data distribution"
        },
        {
          value: "line",
          text: "Line Chart",
          description: "Connected points showing trends over dimensions"
        },
        {
          value: "stacked_bar",
          text: "Stacked Bar Chart",
          description: "Single stacked bar chart showing all campaigns combined"
        },
        {
          value: "multi_stacked",
          text: "Multi-Stacked Bar",
          description: "Multiple stacked bar charts, one for each campaign"
        }
      ]
    };
  },
  async mounted() {
    if (
      window.localStorage.getItem("user") &&
      window.localStorage.getItem("status") != "true"
    ) {
      this.$router.push("/");
    } else {
      // Add UTC if not in list
      if (!this.timezone_list.includes('UTC')) {
        this.timezone_list.push('UTC');
      }
      await this.getSubValue();
      await this.fetchCampaigns();
      this.loadReportSettings();
    }
  },
  methods: {
    async getSubValue() {
      try {
        let response = await SettingService.getSubValue();
        if (response.result == true && response.data && response.data.subValueMappping) {
          const mappedSubValue = response.data.subValueMappping;
          
          // Update projection options with mapped sub values
          if (mappedSubValue && typeof mappedSubValue === 'object') {
            Object.keys(mappedSubValue).forEach((key) => {
              this.projectionOptions = this.projectionOptions.map(item => {
                if (item.value === 'qp_' + key) {
                  return { ...item, text: mappedSubValue[key] };
                }
                return item;
              });
            });
          }
        }
      } catch (error) {
        console.error('Error fetching sub values:', error);
        // Continue even if sub values cannot be fetched
      }
    },
    
    async fetchCampaigns() {
      try {
        let response = await DashboardService.camplist({
          useremail: window.localStorage.getItem("email")
        });
        if (response.result) {
          this.camp_list = response.message.campaign;
          // Convert camp_list object to options array
          // key = campaign name, value = campaign ID
          this.campaignOptions = [];
          for(let [campaignName, campaignId] of Object.entries(this.camp_list)) {
            this.campaignOptions.push({ 
              value: campaignId,  // This is what we track and send in payload
              text: campaignName  // This is what users see
            });
          }
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      }
    },
    
    async loadReportSettings() {
      this.loading = true;
      try {
        const response = await MailerReportsService.getAllReports();
        if (response && response.result !== false && response.data) {
          this.reportSettings = response.data.map(report => ({
            _id: report._id,
            name: report.name,
            enabled: report.enabled,
            reportFrequency: this.frequencyOptions.find(f => f.value === report.reportFrequency) ||
                            { value: report.reportFrequency, text: report.reportFrequency },
            timezone: report.timezone || 'UTC',
            reportRecipientsForAuto: report.reportRecipientsForAuto || [],
            selectedCampaigns: ((report.reportDimensions && report.reportDimensions.camp_id) || []).map(id =>
              this.campaignOptions.find(c => c.value === id) || { value: id, text: id }
            ).filter(Boolean),
            selectedGroupBy: (report.reportProjections || []).map(proj =>
              this.projectionOptions.find(p => p.value === proj)
            ).filter(Boolean),
            selectedMetrics: (report.reportMetrics || []).map(metric =>
              this.metricsOptions.find(m => m.value === metric)
            ).filter(Boolean),
            customGraphs: (report.customGraphs || []).map(graph => ({
              name: graph.name,
              description: graph.description || '',
              type: this.graphTypeOptions.find(t => t.value === graph.type) || this.graphTypeOptions[0],
              selectedCampaigns: (graph.campaignIds || []).map(id =>
                this.currentReport.selectedCampaigns.find(c => c.value === id) ||
                this.campaignOptions.find(c => c.value === id)
              ).filter(Boolean),
              dimensions: (graph.dimensions || []).map(dim =>
                this.currentReport.selectedGroupBy.find(p => p.value === dim) ||
                this.projectionOptions.find(p => p.value === dim)
              ).filter(Boolean),
              metrics: (graph.metrics || []).map(metric =>
                this.currentReport.selectedMetrics.find(m => m.value === metric) ||
                this.metricsOptions.find(m => m.value === metric)
              ).filter(Boolean),
              showLegend: graph.showLegend !== false,
              showDataLabels: graph.showDataLabels || false
            })),
            lastExecutedAt: report.lastExecutedAt || null,
            nextExecutionAt: report.nextExecutionAt || null
          }));
        }
      } catch (error) {
        console.error('Error loading report settings:', error);
        // Fallback to localStorage if API fails
        const savedSettings = localStorage.getItem('autoMailerReports');
        if (savedSettings) {
          this.reportSettings = JSON.parse(savedSettings);
        }
      } finally {
        this.loading = false;
      }
    },
    
    saveReportSettings() {
      // Save to localStorage as backup
      localStorage.setItem('autoMailerReports', JSON.stringify(this.reportSettings));
    },

    openAddReportModal() {
      this.resetCurrentReport();
      this.showAddReportModal = true;
    },
    
    addEmailTag(newTag) {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(newTag)) {
        this.currentReport.reportRecipientsForAuto.push(newTag);
      } else {
        Swal.fire({
          title: "Invalid Email",
          text: "Please enter a valid email address",
          icon: "error"
        });
      }
    },
    
    async saveReport() {
      // Validate report
      if (!this.currentReport.name) {
        Swal.fire({
          title: "Error",
          text: "Please enter a mail format name",
          icon: "error"
        });
        return;
      }

      if (this.currentReport.reportRecipientsForAuto.length === 0) {
        Swal.fire({
          title: "Error",
          text: "Please add at least one recipient email",
          icon: "error"
        });
        return;
      }

      // Validate custom graphs
      if (this.currentReport.customGraphs && this.currentReport.customGraphs.length > 0) {
        this.graphValidationTrigger = true;

        let hasErrors = false;
        for (let i = 0; i < this.currentReport.customGraphs.length; i++) {
          const graph = this.currentReport.customGraphs[i];

          // Check required fields
          if (!graph.name || !graph.type || !graph.metrics || graph.metrics.length === 0) {
            hasErrors = true;
            break;
          }

          // Dimensions are required for all graph types except line charts
          if (graph.type.value !== 'line' && !graph.dimensions) {
            hasErrors = true;
            break;
          }

          // Pie chart specific validation (only single metric)
          if (graph.type && graph.type.value === 'pie') {
            if (Array.isArray(graph.metrics) && graph.metrics.length > 1) {
              hasErrors = true;
              break;
            }
          }
        }

        if (hasErrors) {
          Swal.fire({
            title: "Error",
            text: "Please fill all required fields in custom graphs (name, graph type, and metrics). Note: Dimensions are required for all chart types except line charts. Pie charts can only have one metric.",
            icon: "error"
          });
          return;
        }
      }
      
      this.loading = true;
      try {
        const payload = this.preparePayload(this.currentReport);
        
        let response;
        if (this.editingIndex !== null && this.currentReport._id) {
          // Update existing report
          response = await MailerReportsService.updateReport(this.currentReport._id, payload);
        } else {
          // Create new report
          response = await MailerReportsService.createReport(payload);
        }
        
        if (response && response.result !== false) {
          await this.loadReportSettings();
          this.showAddReportModal = false;
          this.resetCurrentReport();
          
          Swal.fire({
            title: "Success!",
            text: response.message || (this.editingIndex !== null ? "Mail format updated successfully" : "Mail format added successfully"),
            icon: "success"
          });
        } else {
          throw new Error(response.message || 'Operation failed');
        }
      } catch (error) {
        console.error('Error saving report:', error);
        Swal.fire({
          title: "Error",
          text: (error.response && error.response.data && error.response.data.message) || error.message || "Failed to save mail format",
          icon: "error"
        });
      } finally {
        this.loading = false;
      }
    },
    
    editReport(index) {
      this.editingIndex = index;
      this.currentReport = { ...this.reportSettings[index] };
      this.showAddReportModal = true;
    },
    
    async deleteReport(index) {
      const report = this.reportSettings[index];
      
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });
      
      if (result.isConfirmed) {
        this.loading = true;
        try {
          if (report._id) {
            const response = await MailerReportsService.deleteReport(report._id);
            if (response && response.result !== false) {
              await this.loadReportSettings();
              Swal.fire(
                'Deleted!',
                response.message || 'Mail format has been deleted.',
                'success'
              );
            } else {
              throw new Error(response.message || 'Delete failed');
            }
          } else {
            // If no _id, just remove from local array
            this.reportSettings.splice(index, 1);
            this.saveReportSettings();
            Swal.fire(
              'Deleted!',
              'Mail format has been deleted.',
              'success'
            );
          }
        } catch (error) {
          console.error('Error deleting report:', error);
          Swal.fire({
            title: "Error",
            text: (error.response && error.response.data && error.response.data.message) || error.message || "Failed to delete mail format",
            icon: "error"
          });
        } finally {
          this.loading = false;
        }
      }
    },
    
    async toggleReportStatus(index) {
      const report = this.reportSettings[index];
      const newStatus = !report.enabled;
      
      // Update UI immediately for better UX
      this.reportSettings[index].enabled = newStatus;
      
      if (report._id) {
        try {
          const response = await MailerReportsService.toggleReportStatus(report._id, newStatus);
          if (!response || response.result === false) {
            // Revert on failure
            this.reportSettings[index].enabled = !newStatus;
            throw new Error(response.message || 'Toggle failed');
          }
        } catch (error) {
          // Revert on error
          this.reportSettings[index].enabled = !newStatus;
          console.error('Error toggling report status:', error);
          Swal.fire({
            title: "Error",
            text: (error.response && error.response.data && error.response.data.message) || error.message || "Failed to update status",
            icon: "error",
            timer: 2000,
            showConfirmButton: false
          });
        }
      } else {
        // Just save locally if no _id
        this.saveReportSettings();
      }
    },
    
    cancelEdit() {
      this.showAddReportModal = false;
      this.resetCurrentReport();
    },
    
    resetCurrentReport() {
      this.editingIndex = null;
      this.graphValidationTrigger = false;
      this.currentReport = {
        _id: null,
        name: '',
        enabled: true,
        reportFrequency: { value: 'daily', text: 'Daily' },
        timezone: 'UTC',
        reportRecipientsForAuto: [],
        selectedCampaigns: [],
        selectedGroupBy: [
          { value: "campname", text: "Campaign" }
        ],
        selectedMetrics: [
          { value: "browse", text: "Click" },
          { value: "impression", text: "Impression" },
          { value: "accepted", text: "Valid" },
          { value: "discarded", text: "Discarded" },
          { value: "rejected", text: "Invalid" },
          { value: "converted", text: "Converted" },
          { value: "convertedRate", text: "Conversion Rate" },
          { value: "rejectedRate", text: "Invalid Rate" },
          { value: "acceptedRate", text: "Valid Rate" }
        ],
        customGraphs: []
      };
    },
    
    // Helper method to prepare payload for API
    preparePayload(report) {
      const payload = {
        name: report.name,
        enabled: report.enabled,
        reportFrequency: report.reportFrequency.value,
        timezone: report.timezone || 'UTC',
        reportRecipientsForAuto: report.reportRecipientsForAuto,
        reportProjections: report.selectedGroupBy.map(item => item.value),
        reportMetrics: report.selectedMetrics.map(item => item.value),
        reportDimensions: {
          camp_id: report.selectedCampaigns.map(campaign => campaign.value)
        }
      };

      // Add custom graphs if present
      if (report.customGraphs && report.customGraphs.length > 0) {
        payload.customGraphs = report.customGraphs.map(graph => {
          const graphType = graph.type ? graph.type.value : 'bar';
          const isPieChart = graphType === 'pie';

          return {
            name: graph.name,
            type: graphType,
            description: graph.description || '',
            displayMode: graphType === 'multi_stacked' ? 'separate' : 'combined',
            campaignIds: graph.selectedCampaigns ? graph.selectedCampaigns.map(c => c.value) : [],
            dimensions: graphType === 'line' ? [] : (graph.dimensions ? (
              Array.isArray(graph.dimensions)
                ? graph.dimensions.map(dim => dim.value)
                : [graph.dimensions.value]
            ) : []),
            metrics: graph.metrics ? (
              isPieChart
                ? (Array.isArray(graph.metrics) ? [graph.metrics[0].value] : [graph.metrics.value])
                : graph.metrics.map(metric => metric.value)
            ) : [],
            showLegend: graph.showLegend !== false,
            showDataLabels: graph.showDataLabels || false
          };
        });
      }

      return payload;
    },

    // Add new custom graph
    addCustomGraph() {
      if (!this.currentReport.customGraphs) {
        this.currentReport.customGraphs = [];
      }

      this.currentReport.customGraphs.push({
        name: '',
        description: '',
        type: this.graphTypeOptions[0], // Default to bar chart
        selectedCampaigns: [], // Will use report's campaigns if empty
        dimensions: null, // Single dimension selection
        metrics: [],
        showLegend: true,
        showDataLabels: false
      });
    },

    // Remove custom graph
    removeCustomGraph(index) {
      this.currentReport.customGraphs.splice(index, 1);
    },


    // Show graph structure help
    showGraphStructureHelp(graph) {
      const groupByText = graph.dimensions
        ? graph.dimensions.text
        : 'not selected';
      const metricsText = graph.metrics && graph.metrics.length > 0
        ? graph.metrics.map(m => m.text).join(', ')
        : 'not selected';

      let helpTitle = 'Graph Structure';
      let helpContent = '';

      if (graph.type) {
        switch (graph.type.value) {
          case 'bar':
            helpTitle = 'Bar Chart Structure';
            helpContent = `
              <p>Your graph will display grouped bars:</p>
              <ul>
                <li><strong>X-Axis:</strong> ${groupByText}</li>
                <li><strong>Bars:</strong> Each metric (${metricsText}) as separate bars</li>
                <li><strong>Groups:</strong> All campaigns shown together</li>
              </ul>`;
            break;
          case 'pie':
            helpTitle = 'Pie Chart Structure';
            helpContent = `
              <p>Your graph will display a pie chart:</p>
              <ul>
                <li><strong>Slices:</strong> ${groupByText} values</li>
                <li><strong>Size:</strong> Based on ${metricsText}</li>
                <li><strong>Data:</strong> All campaigns combined</li>
              </ul>`;
            break;
          case 'line':
            helpTitle = 'Line Chart Structure';
            helpContent = `
              <p>Your graph will display trend lines:</p>
              <ul>
                <li><strong>X-Axis:</strong> Date/Time</li>
                <li><strong>Lines:</strong> Each metric (${metricsText}) as separate lines</li>
                <li><strong>Grouping:</strong> Data grouped by ${groupByText}</li>
                <li><strong>Data:</strong> All campaigns combined</li>
              </ul>`;
            break;
          case 'stacked_bar':
            helpTitle = 'Stacked Bar Chart Structure';
            helpContent = `
              <p>Your graph will display stacked bars:</p>
              <ul>
                <li><strong>X-Axis:</strong> Campaigns</li>
                <li><strong>Bars:</strong> Each metric (${metricsText}) as separate bars</li>
                <li><strong>Stacked:</strong> ${groupByText} values stacked within each metric bar</li>
              </ul>`;
            break;
          case 'multi_stacked':
            helpTitle = 'Multi-Stacked Bar Structure';
            helpContent = `
              <p>Your graph will display multiple stacked charts:</p>
              <ul>
                <li><strong>Charts:</strong> One separate chart per campaign</li>
                <li><strong>X-Axis:</strong> ${groupByText}</li>
                <li><strong>Stacked:</strong> Metrics (${metricsText}) stacked in each bar</li>
              </ul>`;
            break;
          default:
            helpContent = `
              <p>Graph structure information:</p>
              <ul>
                <li><strong>Dimension:</strong> ${groupByText}</li>
                <li><strong>Metrics:</strong> ${metricsText}</li>
              </ul>`;
        }
      }

      const helpText = `
        <div style="text-align: left;">
          <h5>${helpTitle}</h5>
          ${helpContent}
        </div>
      `;

      Swal.fire({
        title: 'Graph Structure',
        html: helpText,
        icon: 'info',
        width: '600px',
        confirmButtonText: 'Got it!'
      });
    },



    // Handle graph type change to enforce constraints
    onGraphTypeChange(graph) {
      // For all chart types, limit to single dimension
      if (Array.isArray(graph.dimensions) && graph.dimensions.length > 1) {
        graph.dimensions = graph.dimensions[0];
      }

      // For pie charts, also limit to single metric
      if (graph.type && graph.type.value === 'pie') {
        if (Array.isArray(graph.metrics) && graph.metrics.length > 1) {
          graph.metrics = graph.metrics[0];
        }
      }
    },
    
    async executeReportNow(index) {
      
      const report = this.reportSettings[index];
      console.log('Executing report:', report); // Debug log
      Swal.fire({
        title: "Executing Report",
        text: `Executing report: ${report._id}`,
        icon: "info",
        showConfirmButton: false,
        timer: 2000
      });
      if (!report._id) {
        Swal.fire({
          title: "Info",
          text: "This report needs to be saved to the server before it can be executed. Currently it only exists locally.",
          icon: "info"
        });
        return;
      }
      
      const result = await Swal.fire({
        title: 'Execute Report Now?',
        text: "This will send the report immediately to all recipients",
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, send now!'
      });
      
      if (result.isConfirmed) {
        this.loading = true;
        try {
          console.log('Calling API for report ID:', report._id); // Debug log
          const response = await MailerReportsService.executeReportNow(report._id);
          console.log('API Response:', response); // Debug log
          
          if (response && response.result !== false) {
            Swal.fire({
              title: "Success!",
              text: response.message || "Report execution started successfully",
              icon: "success"
            });
            
            // Optionally refresh the report to get updated lastExecutedAt
            await this.loadReportSettings();
          } else {
            throw new Error(response.message || 'Execution failed');
          }
        } catch (error) {
          console.error('Execute report error:', error); // Debug log
          Swal.fire({
            title: "Error",
            text: (error.response && error.response.data && error.response.data.message) || error.message || "Failed to execute report",
            icon: "error"
          });
        } finally {
          this.loading = false;
        }
      }
    },
    
    // View execution history for a report
    async viewExecutionHistory(index) {
      const report = this.reportSettings[index];
      if (!report._id) {
        Swal.fire({
          title: "Info",
          text: "Please save the report first to view history",
          icon: "info"
        });
        return;
      }
      
      // Set default date range (last 30 days)
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      this.historyFilters.endDate = today.toISOString().split('T')[0];
      this.historyFilters.startDate = thirtyDaysAgo.toISOString().split('T')[0];
      this.historyFilters.status = 'all';
      
      this.currentReportHistory = report;
      this.showExecutionHistoryModal = true;
      await this.loadExecutionHistory(1);
    },
    
    // Reload execution history when date range changes
    async reloadExecutionHistory() {
      // Reset to first page and reload data with new date range
      await this.loadExecutionHistory(1);
    },

    // Load execution history with pagination
    async loadExecutionHistory(page = 1) {
      if (!this.currentReportHistory || !this.currentReportHistory._id) return;

      this.loading = true;
      try {
        const params = {
          page: page,
          limit: this.executionHistoryPagination.limit,
          startDate: this.historyFilters.startDate,
          endDate: this.historyFilters.endDate
        };

        const response = await MailerReportsService.getReportHistory(this.currentReportHistory._id, params);

        if (response && response.result !== false) {
          this.executionHistory = response.data || [];
          this.executionHistoryPagination = response.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0
          };

          // Calculate stats
          this.calculateExecutionStats();

          // Apply filters
          this.filterExecutionHistory();
        }
      } catch (error) {
        console.error('Error loading execution history:', error);
        this.executionHistory = [];
      } finally {
        this.loading = false;
      }
    },
    
    // Calculate execution statistics
    calculateExecutionStats() {
      this.executionStats = {
        total: this.executionHistory.length,
        successful: this.executionHistory.filter(e => e.status === 'completed').length,
        failed: this.executionHistory.filter(e => e.status === 'failed').length,
        pending: this.executionHistory.filter(e => e.status === 'pending' || e.status === 'running').length
      };
    },
    
    // Filter execution history
    filterExecutionHistory() {
      let filtered = [...this.executionHistory];
      
      // Filter by status
      if (this.historyFilters.status !== 'all') {
        filtered = filtered.filter(e => e.status === this.historyFilters.status);
      }
      
      // Filter by date range (handled by API, but can add client-side filtering if needed)
      
      this.filteredExecutionHistory = filtered;
    },
    
    // View detailed execution information
    viewExecutionDetails(execution) {
      this.selectedExecutionDetails = execution;
      this.showExecutionDetailsModal = true;
    },
    
    // Refresh execution history
    async refreshExecutionHistory() {
      await this.loadExecutionHistory(this.executionHistoryPagination.page);
    },
    
    // Get status color for badges
    getStatusColor(status) {
      const statusColors = {
        'completed': 'success',
        'successful': 'success',
        'failed': 'danger',
        'error': 'danger',
        'pending': 'warning',
        'running': 'warning',
        'in_progress': 'warning',
        'scheduled': 'info'
      };
      return statusColors[status] || 'secondary';
    },
    
    // Format date time
    formatDateTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    // Format duration
    formatDuration(seconds) {
      if (!seconds) return '-';
      
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ${secs}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
      } else {
        return `${secs}s`;
      }
    },
    
    async cloneReport(index) {
      const report = this.reportSettings[index];
      if (report._id) {
        this.loading = true;
        try {
          const response = await MailerReportsService.cloneReport(report._id);
          if (response && response.result !== false) {
            await this.loadReportSettings();
            Swal.fire({
              title: "Success!",
              text: response.message || "Report cloned successfully",
              icon: "success"
            });
          }
        } catch (error) {
          Swal.fire({
            title: "Error",
            text: (error.response && error.response.data && error.response.data.message) || "Failed to clone report",
            icon: "error"
          });
        } finally {
          this.loading = false;
        }
      } else {
        // Clone locally
        const clonedReport = { ...report, name: `${report.name} (Copy)`, _id: null };
        this.reportSettings.push(clonedReport);
        this.saveReportSettings();
      }
    }
  },
  watch: {
    // Watch for changes in custom graphs to handle constraints
    'currentReport.customGraphs': {
      handler(newGraphs) {
        if (newGraphs && Array.isArray(newGraphs)) {
          newGraphs.forEach(graph => {
            // Apply constraints for all graph types
            this.onGraphTypeChange(graph);
          });
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.table-responsive table {
  min-width: 1200px;
}

.f-10 {
  font-size: 10px;
  font-weight: 500;
  display: block;
  margin-bottom: 5px;
}

.campaign-selector {
  width: 100%;
}

.modal-content-wrapper {
  padding: 0;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #f0f3f5;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  margin-bottom: 8px;
}

.form-label.required::after {
  content: " *";
  color: #e55353;
}

.text-muted {
  font-size: 12px;
  color: #a0aec0 !important;
  margin-top: 4px;
  display: block;
}

/* Multiselect customization */
.multiselect__tags {
  min-height: 38px;
  border: 1px solid #d8dbe0;
  border-radius: 3px;
}

.multiselect__tag {
  background: #321fdb;
  color: white;
}

.multiselect__tag-icon:after {
  color: white;
}

.multiselect__tag-icon:hover {
  background: #2c1bc7;
}

.multiselect__placeholder {
  color: #768192;
  font-size: 14px;
}

.multiselect__input::placeholder {
  color: #768192;
  font-size: 14px;
}

/* Card styling */
.card {
  box-shadow: 0 0 0 1px rgba(63,63,68,.05), 0 1px 3px 0 rgba(63,63,68,.15);
  border: none;
}

.card-body {
  padding: 1.5rem;
}

/* Modal footer buttons */
.modal-footer {
  border-top: 1px solid #f0f3f5;
  padding: 1rem 1.5rem;
}

/* Execution History Styles */
.execution-history-wrapper {
  padding: 0;
}

.stat-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-card.success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
}

.stat-card.warning {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  color: #856404;
}

.stat-card.danger {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

/* Log viewer styles */
.log-viewer {
  background: #1e1e1e;
  border-radius: 4px;
  padding: 15px;
  max-height: 400px;
  overflow-y: auto;
}

.log-content {
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Table responsive */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table th {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #768192;
  border-bottom: 2px solid #e9ecef;
}

.table td {
  font-size: 13px;
  vertical-align: middle;
}

/* Pagination styles */
.pagination {
  justify-content: center;
}

.page-link {
  color: #321fdb;
  cursor: pointer;
}

.page-item.active .page-link {
  background-color: #321fdb;
  border-color: #321fdb;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Action buttons styling */
.action-buttons {
  display: inline-flex;
  gap: 2px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
}

.action-btn {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border: none;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f0f3f5;
  color: #768192;
  font-size: 12px;
  padding: 0;
  outline: none;
  flex-shrink: 0;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.action-btn:active:not(:disabled) {
  transform: translateY(0);
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn.edit-btn:hover:not(:disabled) {
  background: #4285f4;
  color: white;
}

.action-btn.clone-btn:hover:not(:disabled) {
  background: #9c27b0;
  color: white;
}

.action-btn.history-btn:hover:not(:disabled) {
  background: #17a2b8;
  color: white;
}

.action-btn.execute-btn:hover:not(:disabled) {
  background: #28a745;
  color: white;
}

.action-btn.delete-btn:hover:not(:disabled) {
  background: #dc3545;
  color: white;
}

/* Icon adjustments */
.action-btn .icon-text {
  font-size: 16px;
  line-height: 1;
  display: block;
}

/* For play button, make it larger and centered */
.action-btn.execute-btn .icon-text {
  font-size: 18px;
  margin-left: 2px;
}

/* Execution details modal */
.card-subtitle {
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Status badges */
.badge {
  font-size: 11px;
  padding: 4px 8px;
  font-weight: 500;
}

/* Code display */
code {
  background: #f4f4f4;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #e83e8c;
}

/* Scrollbar styles for log viewer */
.log-viewer::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.log-viewer::-webkit-scrollbar-track {
  background: #2d2d2d;
}

.log-viewer::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

.log-viewer::-webkit-scrollbar-thumb:hover {
  background: #666;
}

/* Custom Graphs Styles */
.custom-graph-item {
  margin-bottom: 20px;
}

.border-left-info {
  border-left: 4px solid #17a2b8 !important;
}

.graph-title {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
}

.multiselect-invalid .multiselect__tags {
  border-color: #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.05);
}

.is-invalid {
  border-color: #dc3545 !important;
}

.text-danger {
  color: #dc3545 !important;
  font-size: 11px;
  margin-top: 4px;
}

</style>